import { useCallback, useEffect, useRef, useState } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';

import { useGoHome } from '../../../hooks/useGoHome';
import { useSideMenu } from '../../../hooks/useSideMenu';
import { useToggleCoin } from '../../../hooks/useToggleCoin';
import { intercom } from '../../../services/intercom';
import { useStores } from '../../../stores/useStores';
import { devLog } from '../../../utils/log';

export const useGameIframeEvents = ({ iframeRef }: { iframeRef: React.RefObject<HTMLIFrameElement> }) => {
  const { coinTypeStore, gameStore, payRedeemStore, prizeDropStore, uiStore, minigamesStore, lowBalanceStore } =
    useStores();
  const { handleGoHome } = useGoHome();
  const { toggleCoin } = useToggleCoin();
  const { handleToggle } = useSideMenu();

  const { prizeDropEventByCoin } = prizeDropStore;

  const GAME_IFRAME_LOAD_TIMEOUT_MS = 5000;

  const iframeElRef = useRef<HTMLIFrameElement | null>(null);

  const [isIframeLoading, setIsIframeLoading] = useState(!!gameStore.gameUiUrl);

  const onGameIframeError = () => {
    console.error(`Error loading Game UI iframe`);
    gameStore.setIsGameUiError(true);
    setIsIframeLoading(false);
  };

  const onIframeLoad = useCallback(() => {
    console.log('Game UI iframe loaded');
    setIsIframeLoading(false);
  }, []);

  const onGameUiMessage = useCallback(
    (event: MessageEvent) => {
      const { data, origin } = event;

      if (origin !== import.meta.env.VITE_GAME_UI_URL || data.source !== 'game') {
        return;
      }

      if (gameStore.isGameUiError) {
        devLog(
          `useGameIframeEvents Ignoring message from game UI because it is in error state: ${JSON.stringify(data)}`,
        );
        return;
      }

      devLog(`useGameIframeEvents Received message from game UI: ${JSON.stringify(data)}`);

      switch (data.type) {
        case 'gameUiReadyForMessages': {
          if (data.payload?.type === 'auth') {
            void gameStore.sendCustomTokenToSlotsGameUi();
            return;
          }

          if (data.payload?.type !== 'slots') {
            return;
          }

          console.log('game ui iframe DOM is ready for receiving messages');
          setTimeout(() => {
            void gameStore.handleSlotsGameIframeLoad(iframeElRef.current!);
          }, 1000);
          break;
        }
        case 'openSupport': {
          intercom.handleOpenSupport();
          break;
        }
        case 'goHome': {
          handleGoHome();
          break;
        }
        case 'headerCoinToggleClick': {
          toggleCoin();
          break;
        }
        case 'headerMenuClick': {
          handleToggle();
          break;
        }
        case 'escapeKeyClick': {
          if (uiStore.isVisibleMiniStore) {
            payRedeemStore.closeMiniStore();
          }
          break;
        }
        case 'relaunchGame': {
          if (gameStore.currentGame?.gameId) {
            void gameStore.launchGame(gameStore.currentGame.gameId, coinTypeStore.coinType, handleGoHome);
          } else {
            console.warn(`useGameIframeEvents relaunchGame message missing gameId, data: ${JSON.stringify(data)}`);
          }
          break;
        }
        case 'miniStoreClick': {
          payRedeemStore.toggleMiniStore({
            eventAction: 'click',
            triggeredBy: 'click',
            buttonLocation: 'header',
            clickId: CLICK_IDS.PURCHASE.HEADER_BALANCE_WRAPPER,
          });
          break;
        }
        case 'lowBalanceOpenMiniStore': {
          lowBalanceStore.openMiniStoreOnLowBalance(data.payload?.trigger || 'low_balance');
          break;
        }
        case 'prizeDropTimerEnd': {
          prizeDropStore.resetEvent();
          break;
        }
        case 'headerPrizeDropWidgetClick': {
          if (!prizeDropEventByCoin) {
            return;
          }

          void prizeDropStore.startPrizeDropGame({ type: 'info' });

          break;
        }
        case 'startMinigame': {
          if (!data.payload?.gameId) {
            console.warn(`useGameIframeEvents startMinigame message missing gameId, data: ${JSON.stringify(data)}`);
            return;
          }

          uiStore.setCustomLandingCoinAnimation({
            gcLandingPoint: data.payload.gcLandingPoint,
            scLandingPoint: data.payload.scLandingPoint,
          });

          void minigamesStore.startMinigame(data.payload.gameId);
          break;
        }
        default: {
          console.warn(`useGameIframeEvents Unhandled message from game UI: ${JSON.stringify(data)}`);
        }
      }
    },
    [
      gameStore,
      coinTypeStore,
      handleGoHome,
      toggleCoin,
      handleToggle,
      uiStore,
      payRedeemStore,
      prizeDropStore,
      prizeDropEventByCoin,
      minigamesStore,
      lowBalanceStore,
    ],
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframeElRef.current = iframe;

    devLog('useGameIframeEvents GameIframe listening for gameUiReady message');
    window.addEventListener('message', onGameUiMessage);
    iframe.addEventListener('load', onIframeLoad);

    let timeout: NodeJS.Timeout;

    if (gameStore.gameUiUrl && (isIframeLoading || !gameStore.slotsGameUiIframe)) {
      timeout = setTimeout(() => {
        if (isIframeLoading || !gameStore.slotsGameUiIframe) {
          if (isIframeLoading) {
            console.error('Game UI iframe load timeout');
          } else if (!gameStore.slotsGameUiIframe) {
            console.error('Game UI iframe did not send gameUiReady message in time');
          }

          gameStore.setIsGameUiError(true);
          setIsIframeLoading(false);
        }
      }, GAME_IFRAME_LOAD_TIMEOUT_MS);
    }

    return () => {
      devLog('useGameIframeEvents GameIframe removing gameUiReady message listener');
      window.removeEventListener('message', onGameUiMessage);
      iframe.removeEventListener('load', onIframeLoad);
      clearTimeout(timeout);
    };
  }, [onIframeLoad, iframeRef, onGameUiMessage, isIframeLoading, gameStore, gameStore.slotsGameUiIframe]);

  return {
    isIframeLoading,
    onGameIframeError,
  };
};
